import { mergeMap, map, flatMap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { Observable, BehaviorSubject } from 'rxjs';
import { ActionPlanModel, ActionPlanItem, ReportPdfModel, Inspection } from '../models/index';
import * as _ from 'lodash';
import { ActionPlanDataService } from './action-plan-data.service';
import { ReportService } from './report.service';
import { IndexedDbService } from './indexedDb.service';
import { ApiMode } from '../../utils/enums';
import { DataService } from './data.service';
import { OfflineApiService } from './offlineApi.service';
import { AppConstants } from '../../utils/appConstants.class';
import { BaseService } from './baseService';

@Injectable({
  providedIn: 'root'
})
export class ActionPlanService extends BaseService implements ReportService {
  public fakeReports = [];

  public actionPlanItemStatus: any = [
    { value: 0, label: 'Draft' },
    { value: 100, label: 'Not Started' },
    { value: 150, label: 'In Progress' },
    { value: 200, label: 'On Standby' },
    { value: 250, label: 'Completed' }
  ];

  constructor(
    private apiService: ApiService,
    private dataService: ActionPlanDataService,
    private inspectionDataService: DataService,
    private offlineApi: OfflineApiService
  ) {
    super();
  }

  private actionPlansList: ActionPlanModel[];
  private groupedItemsByPlanId: any[] = [];

  public getActionPlans(): Observable<ActionPlanModel[]> {
    if (this.dataService.isLoaded()) {
      return this.dataService.getActionPlans();
    } else {
      const table = AppConstants.INDEXEDDB_TABLES.actionPlans;
      const lastSyncDate = localStorage.getItem(
        `${table}${AppConstants.LAST_SYNC_DATE}_${this.applicationType}_${this.currentUserId}`
      );
      return this.offlineApi.postSyncList('/actionplans/list', { lastSyncDate }, table).pipe(
        map((data) => {
          return this.dataService.processActionPlans(data);
        })
      );
    }
  }

  public getActionPlan(id: string): Observable<ActionPlanModel> {
    const actionPlan = this.dataService.getActionPlan(id);

    if (actionPlan && actionPlan.isLoaded && actionPlan.inspection && actionPlan.inspection.isLoaded) {
      return new BehaviorSubject(actionPlan).asObservable();
    }

    return this.offlineApi.getSyncUpdates(`/actionplans/${id}`, AppConstants.INDEXEDDB_TABLES.actionPlans, id).pipe(
      flatMap((plan: ActionPlanModel) => {
        plan.isLoaded = true;
        const inspectionId = plan.inspectionId;
        const url = `/inspections/${inspectionId}`;
        return this.offlineApi.getSyncUpdates(url, AppConstants.INDEXEDDB_TABLES.inspections, inspectionId).pipe(
          map((inspection: Inspection) => {
            inspection.isLoaded = true;
            const currentInspection = this.inspectionDataService.processInspection(inspection);
            const currentActionPlan = this.dataService.processActionPlan(plan, currentInspection);
            return currentActionPlan;
          })
        );
      })
    );
  }

  public getActionPlanItem(actionPlanId: string, actionPlanItemId: string): Observable<ActionPlanItem> {
    return this.dataService.getActionPlanItem(actionPlanId, actionPlanItemId);
  }

  public getActionPlanItems(actionPlanId: string): Observable<ActionPlanItem[]> {
    return this.dataService.getActionPlanItems(actionPlanId);
  }

  public updateActionPlanStatus(actionPlanId: string, status: number) {
    return this.apiService.patch(`/actionplans/${actionPlanId}/status`, { status: status }).pipe(
      map((data) => {
        return data;
      })
    );
  }

  public updateActionPlanItemStatus(actionPlanId: string, itemId: string, status: number) {
    return this.apiService.patch(`/actionsplans/${actionPlanId}/actionsplansitems/${itemId}/status/${status}`).pipe(
      map((data) => {
        this.dataService.updateActionPlanItemStatus(actionPlanId, itemId, data);
      })
    );
  }

  public updateActionPlanItemPriority(actionPlanId: string, itemId: string, priority: number) {
    return this.apiService.patch(`/actionsplans/${actionPlanId}/actionsplansitems/${itemId}/status/${priority}`).pipe(
      map((data) => {
        return data;
      })
    );
  }

  public downloadReport(id: string): any {
    const url = `api/reports/reportItems/${id}/download`;
    window.open(url, '_blank');
  }

  public downloadReportByType(entityId: string, type: number): any {
    const url = `api/reports/${entityId}/type/${type}/download`;
    window.open(url, '_blank');
  }

  public getReports(id: string): Observable<ReportPdfModel[]> {
    const url = `/reports/${id}`;
    return this.apiService.get(url).pipe(
      map((data: ReportPdfModel[]) => {
        if (this.fakeReports.length > 0) {
          data.unshift(...this.fakeReports);
          this.fakeReports = [];
        }
        return data.map((r) => {
          const report = new ReportPdfModel();
          Object.assign(report, r);
          report.items.sort((a, b) => {
            if (a.type < b.type) {
              return -1;
            } else if (a.type > b.type) {
              return 1;
            }
            return 0;
          });
          return report;
        });
      })
    );
  }

  public createReport(id: string, lang: string): Observable<any> {
    const url = `/reports/actionplans/${id}/create/standard/v1/${lang}`;
    const fakeReport = new ReportPdfModel();
    fakeReport.name = 'Report name in progress';
    fakeReport.generationDate = new Date();
    fakeReport.status = 100;
    fakeReport.items = [];
    this.fakeReports.push(fakeReport);
    return this.apiService.get(url);
  }

  public getActionPlanItemStatus() {
    return this.actionPlanItemStatus;
  }

  public getValueByIndex(array: any, index: any): string {
    return array[index];
  }

  getValue(value: any, array: any): string {
    const obj = _.find(array, ['value', value]);
    return obj ? obj.label : '';
  }

  public changeInputLanguage(id: string, lang: string): Observable<ActionPlanModel> {
    const url = `/actionplans/${id}/inputlanguage/${lang}`;
    return this.apiService.post(url, {});
  }
}
